import { printOrderBookTableCell } from "src/helper";
import {
  isOrderBookCellOutsideRange,
  isOrderBookPriceOutsideRange,
} from "src/Tables/Order/orderBookPriceRange";
import { OrderBookCellField } from "src/Tables/Order/orderBookHighlights";
import ime from "src/Assets/Images/ime.jpeg";
import tsetmcIcon from "src/Assets/Images/tsetmc.jpg";
import { useMemo } from "react";
import { BestLimits, OrderBook } from "src/Interfaces/orderInterfaces";
import { IOpenOrder } from "src/Interfaces/marketmaking";

interface OrderBookTableProps {
  tableInfo?: OrderBook;
  tableHeaderClassName?: string;
  openOrders?: IOpenOrder[];
  logo?: string;
}

const getDepthPercent = (value?: number | null, maxValue?: number) => {
  if (!value || !maxValue || maxValue <= 0) return 0;
  return Math.max(8, (value / maxValue) * 100);
};

const OrderBookTable = ({
  tableInfo,
  tableHeaderClassName,
  openOrders,
  logo,
}: OrderBookTableProps) => {
  const getCellClassName = (
    row: BestLimits,
    field: OrderBookCellField,
    className = "",
  ) =>
    `ob-cell ${className}${
      isOrderBookCellOutsideRange(row, field, tableInfo)
        ? " order-book-out-of-range"
        : ""
    }`;

  const rows = useMemo(() => {
    const data = tableInfo?.bestLimitsDto?.bestLimits || [];
    const buyMax =
      data.reduce(
        (max, item) => Math.max(max, Number(item.qTitMeDem) || 0),
        0,
      ) || 0;
    const sellMax =
      data.reduce(
        (max, item) => Math.max(max, Number(item.qTitMeOf) || 0),
        0,
      ) || 0;

    const normalized = data.map((row, index) => ({
      ...row,
      row: index,
      depthBuy: isOrderBookPriceOutsideRange(row.pMeDem, tableInfo)
        ? 0
        : getDepthPercent(row.qTitMeDem, buyMax),
      depthSell: isOrderBookPriceOutsideRange(row.pMeOf, tableInfo)
        ? 0
        : getDepthPercent(row.qTitMeOf, sellMax),
    }));

    while (normalized.length < 5) {
      normalized.push({
        id: normalized.length,
        insCode: null,
        number: null,
        pMeDem: null,
        pMeOf: null,
        qTitMeDem: null,
        qTitMeOf: null,
        row: normalized.length,
        zOrdMeDem: null,
        zOrdMeOf: null,
        depthBuy: 0,
        depthSell: 0,
      });
    }

    return normalized;
  }, [tableInfo]);

  const image = useMemo(() => (logo ? ime : tsetmcIcon), [logo]);

  return (
    <div className="orderbook-card" dir="rtl">
      <div className="orderbook-meta">
        <div className="orderbook-meta-main">
          <div className="orderbook-meta-row">
            {tableInfo?.tseLink ? (
              <a
                className="orderbook-meta-symbol-link"
                href={tableInfo.tseLink}
                target="_blank"
                rel="noreferrer noopener"
              >
                <span className="orderbook-meta-symbol">
                  {tableInfo?.namad || "-"}
                </span>
                <img className="orderbook-meta-icon" src={image} alt="tsetmc" />
              </a>
            ) : (
              <div className="orderbook-meta-row">
                <span className="orderbook-meta-symbol">
                  {tableInfo?.namad || "-"}
                </span>
                <img className="orderbook-meta-icon" src={image} alt="tsetmc" />
              </div>
            )}
          </div>
          {tableInfo?.fullName ? (
            <div className="orderbook-meta-fullname">{tableInfo.fullName}</div>
          ) : null}
        </div>
        <div className="orderbook-meta-status">
          <span>{tableInfo?.dueDate || ""}</span>
        </div>
      </div>

      <div className="orderbook-grid">
        <section className="buy-side" aria-label="buy orderbook">
          <div className="ob-head">
            <span className="ob-cell">تعداد</span>
            <span className="ob-cell">حجم</span>
            <span className="ob-cell buy-label">خرید</span>
          </div>

          {rows.map((row) => (
            <div className="ob-row" key={`buy-${row.row}`}>
              <div className={getCellClassName(row, "zOrdMeDem")}>
                {printOrderBookTableCell(row.zOrdMeDem)}
              </div>
              <div
                className={getCellClassName(row, "qTitMeDem", "volume-cell")}
                style={{ ["--depth" as any]: `${row.depthBuy}%` }}
              >
                {printOrderBookTableCell(row.qTitMeDem)}
              </div>
              <div className={getCellClassName(row, "pMeDem", "price-cell")}>
                {printOrderBookTableCell(row.pMeDem)}
              </div>
            </div>
          ))}
        </section>

        <section className="sell-side" aria-label="sell orderbook">
          <div className="ob-head">
            <span className="ob-cell sell-label">فروش</span>
            <span className="ob-cell">حجم</span>
            <span className="ob-cell">تعداد</span>
          </div>

          {rows.map((row) => (
            <div className="ob-row" key={`sell-${row.row}`}>
              <div className={getCellClassName(row, "pMeOf", "price-cell")}>
                {printOrderBookTableCell(row.pMeOf)}
              </div>
              <div
                className={getCellClassName(row, "qTitMeOf", "volume-cell")}
                style={{ ["--depth" as any]: `${row.depthSell}%` }}
              >
                {printOrderBookTableCell(row.qTitMeOf)}
              </div>
              <div className={getCellClassName(row, "zOrdMeOf")}>
                {printOrderBookTableCell(row.zOrdMeOf)}
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default OrderBookTable;
